import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Star, CreditCard, Shield, Mail } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useSeo from '@/hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public renderer for marketplace CMS pages created in the Master editor.
 * Any page saved at /master/marketplace/pages with slug `foo` becomes
 * publicly reachable at /p/foo. Blocks are rendered with the SAME visual
 * system used across the marketplace — so "home" slug would also render
 * here, but we redirect /p/home to / to keep the canonical homepage.
 */
export default function MarketplacePage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        // Master can preview unpublished drafts via ?preview=1
        const qs = new URLSearchParams(window.location.search);
        const isPreview = qs.get('preview') === '1';
        const masterToken = localStorage.getItem('masterToken');
        const url = isPreview && masterToken
          ? `${API}/master/marketplace/pages/${slug}/preview`
          : `${API}/marketplace/pages/${slug}`;
        const headers = isPreview && masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
        const res = await fetch(url, { headers });
        if (res.ok) {
          const data = await res.json();
          setPage(data);
          setStatus('ok');
        } else {
          setStatus('notfound');
        }
      } catch {
        setStatus('notfound');
      }
    })();
  }, [slug]);

  useSeo({
    title: page?.title ? `${page.title} · EntrepreNatives` : 'EntrepreNatives',
    description: page?.meta_description || 'A custom page on the EntrepreNatives Indigenous marketplace.',
    canonical: page?.slug ? `https://entreprenatives.com/p/${page.slug}` : undefined,
  });

  if (slug === 'home') return <Navigate to="/" replace />;

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#0E0E23] text-white">
        <Navbar />
        <div className="flex items-center justify-center py-32 text-[#9CA3AF]">Loading…</div>
        <Footer />
      </div>
    );
  }

  if (status === 'notfound' || !page) {
    return (
      <div className="min-h-screen bg-[#0E0E23] text-white">
        <Navbar />
        <div className="container-main py-32 text-center">
          <h1 className="font-syne text-4xl font-bold mb-3">Page not found</h1>
          <p className="text-[#9CA3AF] font-outfit mb-8">The page you\u2019re looking for doesn\u2019t exist or was unpublished.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold">
            Back to home <ArrowRight size={16} />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const blocks = (page.blocks || [])
    .filter(b => b.is_visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="min-h-screen bg-[#0E0E23] text-white" data-testid={`marketplace-page-${page.slug}`}>
      <Navbar />
      <main className="pt-0">
        {blocks.length === 0 ? (
          <div className="container-main py-20 text-center text-[#9CA3AF]">
            This page is empty. Add some blocks in the editor to see content here.
          </div>
        ) : (
          blocks.map(block => <BlockRenderer key={block.id} block={block} />)
        )}
      </main>
      <Footer />
    </div>
  );
}

function BlockRenderer({ block }) {
  const c = block.content || {};
  switch (block.type) {
    case 'marketplace_hero':
      return <HeroBlock c={c} />;
    case 'category_grid':
      return <CategoryGridBlock c={c} />;
    case 'featured_vendors':
      return <FeaturedVendorsBlock c={c} />;
    case 'how_it_works':
      return <HowItWorksBlock c={c} />;
    case 'community_impact':
      return <CommunityImpactBlock c={c} />;
    case 'custom_text':
      return <CustomTextBlock c={c} />;
    case 'custom_image':
      return <CustomImageBlock c={c} />;
    case 'video_embed':
      return <VideoEmbedBlock c={c} />;
    case 'gallery':
      return <GalleryBlock c={c} />;
    case 'testimonial':
      return <TestimonialBlock c={c} />;
    case 'email_signup':
      return <EmailSignupBlock c={c} />;
    case 'cta_banner':
      return <CtaBannerBlock c={c} />;
    case 'embed':
      return <EmbedBlock c={c} />;
    case 'spacer':
      return <div style={{ height: `${c.height || 80}px` }} />;
    default:
      return null;
  }
}

const Holo = ({ children }) => (
  <span
    style={{
      background: 'linear-gradient(90deg,#ff6ef7,#66d4ff,#6eff9e,#fff066,#ff6ef7)',
      backgroundSize: '200% auto',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      animation: 'holoText 3s linear infinite',
    }}
  >
    {children}
    <style>{`@keyframes holoText{0%{background-position:0% center}100%{background-position:200% center}}`}</style>
  </span>
);

function HeroBlock({ c }) {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center text-center py-20 px-4" style={{ background: 'radial-gradient(circle at 50% 30%, rgba(139,92,246,0.2) 0%, rgba(0,0,0,0) 65%)' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl">
        <h1 className="font-syne text-5xl md:text-7xl font-bold leading-tight mb-6">
          {c.title_line1 && <>{c.title_line1}<br /></>}
          {c.title_line2 && <><Holo>{c.title_line2}</Holo><br /></>}
          {c.title_line3}
        </h1>
        {c.subtitle && <p className="text-[#C4B5FD] font-outfit text-lg md:text-xl mb-10 max-w-2xl mx-auto">{c.subtitle}</p>}
        <div className="flex flex-wrap gap-4 justify-center">
          {c.cta_primary_text && (
            <Link to={c.cta_primary_link || '#'} className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold inline-flex items-center gap-2">
              {c.cta_primary_text} <ArrowRight size={16} />
            </Link>
          )}
          {c.cta_secondary_text && (
            <Link to={c.cta_secondary_link || '#'} className="px-8 py-4 rounded-xl border border-white/20 text-white font-bold inline-flex items-center gap-2 hover:bg-white/5">
              {c.cta_secondary_text}
            </Link>
          )}
        </div>
      </motion.div>
    </section>
  );
}

function CategoryGridBlock({ c }) {
  return (
    <section className="py-20 px-4">
      <div className="container-main">
        <h2 className="font-syne text-4xl md:text-5xl font-bold text-center mb-3">
          {c.title} <Holo>{c.title_highlight}</Holo>
        </h2>
        {c.subtitle && <p className="text-[#C4B5FD] font-outfit text-center mb-12">{c.subtitle}</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {(c.cards || []).map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#8B5CF6]/40 transition"
            >
              <h3 className="font-syne font-bold text-lg mb-1">{card.name}</h3>
              <p className="text-[#9CA3AF] font-outfit text-sm">{card.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedVendorsBlock({ c }) {
  return (
    <section className="py-20 px-4 bg-gradient-to-b from-transparent via-[#8B5CF6]/[0.03] to-transparent">
      <div className="container-main">
        <h2 className="font-syne text-4xl md:text-5xl font-bold text-center mb-3">
          {c.title} <Holo>{c.title_highlight}</Holo> {c.title_suffix}
        </h2>
        {c.subtitle && <p className="text-[#C4B5FD] font-outfit text-center mb-12">{c.subtitle}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {(c.vendors || []).map((v, i) => (
            <div key={i} className="p-5 rounded-2xl bg-white/[0.04] border border-white/10">
              <h3 className="font-syne font-bold mb-1">{v.name}</h3>
              <p className="text-[#9CA3AF] text-xs font-outfit mb-2">{v.specialty}</p>
              {v.rating && (
                <div className="inline-flex items-center gap-1 text-[#FCD34D] text-xs">
                  <Star size={12} className="fill-current" /> {v.rating}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksBlock({ c }) {
  const iconMap = { search: Search, 'credit-card': CreditCard, shield: Shield };
  return (
    <section className="py-20 px-4">
      <div className="container-main">
        <h2 className="font-syne text-4xl md:text-5xl font-bold text-center mb-3">
          {c.title} <Holo>{c.title_highlight}</Holo>
        </h2>
        {c.subtitle && <p className="text-[#C4B5FD] font-outfit text-center mb-12">{c.subtitle}</p>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {(c.steps || []).map((step, i) => {
            const Icon = iconMap[step.icon] || Star;
            return (
              <div key={i} className="p-6 rounded-2xl bg-white/[0.04] border border-white/10">
                <div className="text-[#FCD34D] font-mono text-sm mb-3">{step.number}</div>
                <Icon className="w-8 h-8 text-[#A78BFA] mb-3" />
                <h3 className="font-syne font-bold text-lg mb-2">{step.title}</h3>
                <p className="text-[#9CA3AF] font-outfit text-sm">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CommunityImpactBlock({ c }) {
  return (
    <section className="py-20 px-4">
      <div className="container-main grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-syne text-4xl md:text-5xl font-bold leading-tight mb-5">
            {c.title_line1} <Holo>{c.title_highlight}</Holo> {c.title_line3}
          </h2>
          {(c.paragraphs || []).map((p, i) => (
            <p key={i} className="text-[#C4B5FD] font-outfit mb-4">{p}</p>
          ))}
          {c.cta_text && (
            <Link to={c.cta_link || '#'} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold">
              {c.cta_text} <ArrowRight size={14} />
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {(c.stats || []).map((s, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white/[0.04] border border-white/10 text-center">
              <div className="font-syne text-3xl font-bold mb-1">{s.holographic ? <Holo>{s.value}</Holo> : s.value}</div>
              <div className="text-[#9CA3AF] text-xs font-mono uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CustomTextBlock({ c }) {
  const align = c.alignment === 'left' ? 'text-left' : c.alignment === 'right' ? 'text-right' : 'text-center';
  return (
    <section className={`py-16 px-4 ${align}`}>
      <div className="container-main max-w-3xl mx-auto">
        {c.title && <h2 className="font-syne text-3xl md:text-4xl font-bold mb-4">{c.title}</h2>}
        {c.text && <div className="text-[#C4B5FD] font-outfit leading-relaxed whitespace-pre-wrap">{c.text}</div>}
      </div>
    </section>
  );
}

function CustomImageBlock({ c }) {
  if (!c.image_url) return null;
  return (
    <section className="py-12 px-4">
      <div className="container-main max-w-4xl mx-auto">
        <img src={c.image_url} alt={c.caption || ''} className="w-full rounded-2xl" />
        {c.caption && <p className="text-[#9CA3AF] text-sm font-outfit text-center mt-3">{c.caption}</p>}
      </div>
    </section>
  );
}

function VideoEmbedBlock({ c }) {
  if (!c.video_url) return null;
  const videoId = (c.video_url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/) || [])[1];
  return (
    <section className="py-12 px-4">
      <div className="container-main max-w-4xl mx-auto">
        {c.title && <h3 className="font-syne font-bold text-xl mb-3 text-center">{c.title}</h3>}
        <div className="aspect-video rounded-2xl overflow-hidden border border-white/10">
          {videoId ? (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title={c.title || 'Video'}
              className="w-full h-full"
              allowFullScreen
            />
          ) : (
            <video src={c.video_url} controls className="w-full h-full" />
          )}
        </div>
        {c.caption && <p className="text-[#9CA3AF] text-sm font-outfit text-center mt-3">{c.caption}</p>}
      </div>
    </section>
  );
}

function GalleryBlock({ c }) {
  if (!c.images?.length) return null;
  return (
    <section className="py-12 px-4">
      <div className="container-main">
        {c.title && <h3 className="font-syne font-bold text-2xl mb-6 text-center">{c.title}</h3>}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {c.images.map((img, i) => (
            <img key={i} src={typeof img === 'string' ? img : img.url} alt="" className="w-full aspect-square object-cover rounded-xl border border-white/10" />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialBlock({ c }) {
  return (
    <section className="py-16 px-4">
      <div className="container-main max-w-3xl mx-auto text-center">
        <div className="text-5xl text-[#8B5CF6] mb-4 font-serif">"</div>
        <p className="text-[#C4B5FD] font-outfit text-xl md:text-2xl italic mb-6 leading-relaxed">{c.quote}</p>
        <p className="font-syne font-bold">{c.author}</p>
        {c.role && <p className="text-[#9CA3AF] text-sm font-outfit">{c.role}</p>}
      </div>
    </section>
  );
}

function EmailSignupBlock({ c }) {
  return (
    <section className="py-16 px-4">
      <div className="container-main max-w-2xl mx-auto p-10 rounded-3xl bg-gradient-to-br from-[#8B5CF6]/15 to-[#3B82F6]/10 border border-white/10 text-center">
        <Mail className="w-10 h-10 text-[#A78BFA] mx-auto mb-4" />
        {c.title && <h3 className="font-syne text-2xl md:text-3xl font-bold mb-2">{c.title}</h3>}
        {c.subtitle && <p className="text-[#C4B5FD] font-outfit mb-6">{c.subtitle}</p>}
        <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
          <input type="email" placeholder="Enter your email" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#64748B]" />
          <button type="submit" className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold">
            {c.button_text || 'Subscribe'}
          </button>
        </form>
      </div>
    </section>
  );
}

function CtaBannerBlock({ c }) {
  const bg = c.background === 'gold'
    ? 'linear-gradient(135deg,#F59E0B 0%,#D97706 100%)'
    : 'linear-gradient(135deg,#8B5CF6 0%,#3B82F6 100%)';
  return (
    <section className="py-16 px-4">
      <div className="container-main max-w-5xl mx-auto p-10 rounded-3xl text-center" style={{ background: bg }}>
        {c.title && <h3 className="font-syne text-3xl md:text-4xl font-bold mb-3">{c.title}</h3>}
        {c.subtitle && <p className="font-outfit mb-6 opacity-90">{c.subtitle}</p>}
        {c.button_text && (
          <Link to={c.button_link || '#'} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-[#0E0E23] font-bold">
            {c.button_text} <ArrowRight size={16} />
          </Link>
        )}
      </div>
    </section>
  );
}

function EmbedBlock({ c }) {
  if (!c.url) return null;
  return (
    <section className="py-12 px-4">
      <div className="container-main max-w-5xl mx-auto">
        {c.title && <h3 className="font-syne font-bold text-xl mb-4 text-center">{c.title}</h3>}
        <iframe src={c.url} title={c.title || 'Embed'} className="w-full rounded-2xl border border-white/10" style={{ height: c.height || '400px' }} />
      </div>
    </section>
  );
}
